/**
 * Ειδοποίηση «η πλατφόρμα ακύρωσε την παραγγελία».
 *
 * ΓΙΑΤΙ: όταν το Wolt ακυρώνει μια παραγγελία, ο server την κλείνει αυτόματα
 * (backend/utils/woltCancellation). Αν όμως ήταν ήδη ΑΠΟΔΕΚΤΗ, η κουζίνα μπορεί
 * να την ετοιμάζει — μια σιωπηλή αλλαγή κατάστασης στη λίστα δεν τη βλέπει
 * κανείς, και το φαγητό γίνεται για διανομέα που δεν θα έρθει ποτέ. Γι' αυτό:
 * ήχος + παράθυρο που μένει μέχρι να πατηθεί «Το είδα».
 *
 * Φορτώνεται ΜΙΑ φορά (AppWithProviders), ώστε να φαίνεται σε κάθε οθόνη του
 * καταστήματος — ταμείο, διαχείριση, κουζίνα. Όχι σε σερβιτόρους, οδηγούς, kiosk.
 * Το συμβάν στέλνεται μόνο όταν ο server έμαθε την ακύρωση (webhook), ΜΙΑ φορά.
 */

import React, { useEffect, useRef, useState } from 'react';
import { formatMoney } from '../utils/formatMoney';
import { Ban, Check } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import efoodLogo from '../assets/logos/logo_efood.png';
import woltLogo from '../assets/logos/logo_wolt.png';
import boxLogo from '../assets/logos/logo_box.png';

interface CancelAlert {
  order_id: number;
  short_code?: string | null;
  platform: string;
  total_amount?: string | number | null;
  customer_name?: string | null;
  was_accepted: boolean;
  timestamp?: string;
}

const LOGOS: Record<string, string> = { wolt: woltLogo, efood: efoodLogo, box: boxLogo };
const NAMES: Record<string, string> = { wolt: 'Wolt', efood: 'efood', box: 'BOX' };
const HIDDEN_ROLES = ['waiter', 'senior_waiter', 'driver', 'kiosk'];
const SOUND_MS = 8000;

const PlatformCancelAlert: React.FC = () => {
  const { employee } = useAuth();
  const { socket, isConnected } = useSocket();
  const [alerts, setAlerts] = useState<CancelAlert[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hidden = HIDDEN_ROLES.includes(String((employee as any)?.role || ''));

  useEffect(() => {
    if (!socket || !isConnected || hidden) return;
    const onCancel = (data: CancelAlert) => {
      if (!data?.order_id) return;
      setAlerts(prev => (prev.some(a => a.order_id === data.order_id) ? prev : [...prev, data]));
    };
    socket.on('platform-order-cancelled', onCancel);
    return () => { socket.off('platform-order-cancelled', onCancel); };
  }, [socket, isConnected, hidden]);

  const current = alerts[0] || null;

  // Ήχος μόνο όταν ήταν ήδη αποδεκτή (η κουζίνα μπορεί να την ετοιμάζει) —
  // μια ακύρωση πριν την αποδοχή δεν θέλει καμία ενέργεια, αρκεί το παράθυρο.
  useEffect(() => {
    const stop = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
    if (!current?.was_accepted) { stop(); return; }
    try {
      const audio = new Audio('/notification.mp3');
      audio.loop = true;
      audio.play().catch(() => {});
      audioRef.current = audio;
    } catch { /* χωρίς ήχο, δεν πειράζει */ }
    const timer = setTimeout(stop, SOUND_MS);
    return () => { clearTimeout(timer); stop(); };
  }, [current?.order_id, current?.was_accepted]);

  if (hidden || !current) return null;

  const platformName = NAMES[current.platform] || current.platform;
  const code = current.short_code ? `#${current.short_code}` : `#${current.order_id}`;
  const amount = Number(current.total_amount || 0);
  const at = current.timestamp
    ? new Date(current.timestamp).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })
    : null;
  const acknowledge = () => setAlerts(prev => prev.slice(1));

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="platform-cancel-title"
      className="cashier-overlay fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3"
    >
      <div className="cashier-dialog bg-surface-1 rounded-2xl border-2 border-red-500/70 w-full max-w-md shadow-2xl shadow-red-900/30 overflow-hidden">
        <div className="px-5 py-3.5 bg-red-600 text-white flex items-center gap-3">
          <Ban className="w-6 h-6 shrink-0" />
          <span id="platform-cancel-title" className="font-bold text-lg flex-1">Ακύρωση από {platformName}</span>
          {alerts.length > 1 && (
            <span className="text-xs font-semibold bg-white/20 rounded-full px-2 py-0.5">+{alerts.length - 1} ακόμα</span>
          )}
        </div>

        <div className="px-5 py-5 space-y-4">
          <div className="flex items-center gap-3">
            {LOGOS[current.platform] && (
              <img src={LOGOS[current.platform]} alt={platformName} className="w-10 h-10 object-contain shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-2xl font-bold text-t1 leading-tight">{code}</div>
              {current.customer_name && <div className="text-sm text-t2 truncate">{current.customer_name}</div>}
            </div>
            {amount > 0 && <div className="text-xl font-bold text-t1 shrink-0">{formatMoney(amount)}</div>}
          </div>

          {current.was_accepted ? (
            <div className="rounded-xl border border-red-300 dark:border-red-700/60 bg-red-100 dark:bg-red-950/40 px-4 py-3">
              <p className="text-base font-bold text-red-700 dark:text-red-300">Σταματήστε την ετοιμασία.</p>
              <p className="text-sm text-red-800/90 dark:text-red-200/90 mt-1">
                Η παραγγελία είχε ήδη γίνει δεκτή, αλλά το {platformName} την ακύρωσε — δεν θα έρθει διανομέας.
                Δεν χρειάζεται απόδειξη.
              </p>
            </div>
          ) : (
            <p className="text-sm text-t2">
              Ακυρώθηκε από το {platformName} πριν γίνει δεκτή. Δεν χρειάζεται καμία ενέργεια.
            </p>
          )}

          {at && <p className="text-xs text-t3">Ακυρώθηκε στις {at}</p>}

          <button
            type="button"
            onClick={acknowledge}
            autoFocus
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-base font-bold text-white bg-slate-700 hover:bg-slate-600 dark:bg-slate-600 dark:hover:bg-slate-500 transition-colors active:scale-[0.98]"
          >
            <Check className="w-5 h-5" />
            Το είδα
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlatformCancelAlert;
